import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { osFetch } from "../api";
import { toast } from "sonner";
import { Sparkles, Loader2, Send, ArrowRight, ChevronDown, ChevronRight, MessageSquare, Compass } from "lucide-react";

function ConfidenceBar({ value }) {
  const c = value >= 70 ? "#16C784" : value >= 40 ? "#F0B90B" : "#F6465D";
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-24 rounded-full bg-white/5 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${value}%`, background: c }} /></div>
      <span className="text-[10px] font-mono" style={{ color: c }}>{value}% confidence</span>
    </div>
  );
}

function EntityChips({ items, label }) {
  if (!items?.length) return null;
  return (
    <div className="mt-3">
      <p className="text-[10px] font-mono uppercase tracking-widest text-slate-600 mb-1.5">{label}</p>
      <div className="flex flex-wrap gap-1.5">
        {items.map((s) => (
          <Link key={`${s.type}:${s.id}`} to={s.route} className="text-[11px] font-mono text-slate-300 border border-white/10 rounded-full px-2.5 py-1 hover:text-[#00F0FF] hover:border-[#00F0FF]/40 transition-colors" data-testid={`ask-source-${s.id}`}>
            <span className="text-slate-600">{s.type}·</span>{s.title}
          </Link>
        ))}
      </div>
    </div>
  );
}

function AnswerCard({ turn }) {
  const [showReason, setShowReason] = useState(false);
  return (
    <div className="os-glass rounded-2xl p-5 os-fade" data-testid={`ask-answer-${turn.id}`}>
      <p className="text-white text-[15px] leading-relaxed whitespace-pre-wrap" data-testid="ask-answer-text">{turn.answer}</p>
      <div className="flex items-center gap-4 mt-3 flex-wrap">
        <ConfidenceBar value={turn.confidence} />
        {turn.reasoning && (
          <button onClick={() => setShowReason((s) => !s)} className="text-[11px] font-mono text-slate-500 hover:text-white flex items-center gap-1" data-testid="ask-reasoning-toggle">
            {showReason ? <ChevronDown size={12} /> : <ChevronRight size={12} />} Reasoning
          </button>
        )}
      </div>
      {showReason && turn.reasoning && <p className="text-xs text-slate-400 mt-2 border-l-2 border-[#0055FF]/40 pl-3">{turn.reasoning}</p>}
      <EntityChips items={turn.sources} label="Source entities" />
      <EntityChips items={turn.related} label="Related" />
      {turn.suggested_next_actions?.length > 0 && (
        <div className="mt-3">
          <p className="text-[10px] font-mono uppercase tracking-widest text-slate-600 mb-1.5">Suggested next actions</p>
          <ul className="space-y-1">
            {turn.suggested_next_actions.map((a, i) => <li key={i} className="text-sm text-slate-300 flex gap-2"><ArrowRight size={13} className="text-[#00F0FF] mt-0.5 shrink-0" />{a}</li>)}
          </ul>
        </div>
      )}
      <p className="text-[10px] font-mono text-slate-700 mt-4">ArcaOS · {turn.generated_by_run?.slice(0, 8)} · {new Date(turn.created_at).toLocaleString()}</p>
    </div>
  );
}

export default function AskArca() {
  const [q, setQ] = useState("");
  const [thread, setThread] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const endRef = useRef(null);

  useEffect(() => { osFetch("/ask/suggestions").then(setSuggestions).catch(() => {}); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [thread, busy]);

  const submit = async (question) => {
    const text = (question || q).trim();
    if (!text || busy) return;
    setQ(""); setBusy(true);
    setThread((t) => [...t, { role: "user", text, id: `u-${Date.now()}` }]);
    try {
      const r = await osFetch("/ask", { method: "POST", body: { question: text, session_id: sessionId } });
      setSessionId(r.session_id);
      setThread((t) => [...t, { role: "assistant", ...r }]);
    } catch (e) { toast.error(e.message); setThread((t) => [...t, { role: "assistant", id: `e-${Date.now()}`, answer: "Something went wrong reaching ArcaOS. Please try again.", confidence: 0, created_at: new Date().toISOString() }]); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto flex flex-col h-[calc(100vh-140px)]" data-testid="os-ask">
      <div className="mb-4">
        <p className="font-mono text-[11px] uppercase tracking-widest text-[#00F0FF] mb-1 flex items-center gap-2"><Sparkles size={13} /> Ask ArcaOS</p>
        <h1 className="font-display font-extrabold text-3xl">Ask your company anything</h1>
        <p className="text-slate-500 mt-1 text-sm flex items-center gap-2"><Compass size={13} className="text-[#00F0FF]" /> Grounded in your live knowledge graph — every answer cites its sources and says honestly when something isn't recorded.</p>
      </div>

      <div className="flex-1 overflow-auto space-y-4 pr-1" data-testid="ask-thread">
        {thread.length === 0 && (
          <div className="os-glass rounded-2xl p-6" data-testid="ask-empty">
            <p className="text-slate-400 text-sm mb-3">Try one of these:</p>
            <div className="flex flex-wrap gap-2">
              {suggestions.map((s) => (
                <button key={s} onClick={() => submit(s)} className="text-xs text-slate-300 border border-white/10 rounded-full px-3 py-1.5 hover:text-white hover:border-[#0055FF]/50 transition-colors" data-testid={`ask-suggestion`}>{s}</button>
              ))}
            </div>
          </div>
        )}
        {thread.map((t) => t.role === "user"
          ? <div key={t.id} className="flex justify-end"><div className="bg-[#0055FF] text-white rounded-2xl rounded-br-sm px-4 py-2.5 max-w-[80%] text-sm" data-testid="ask-question">{t.text}</div></div>
          : <AnswerCard key={t.id} turn={t} />)}
        {busy && <div className="flex items-center gap-2 text-slate-400 text-sm"><Loader2 size={15} className="animate-spin" /> ArcaOS is thinking…</div>}
        <div ref={endRef} />
      </div>

      <div className="mt-4 flex items-center gap-2 os-glass rounded-2xl p-2">
        <MessageSquare size={16} className="text-slate-500 ml-2" />
        <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} placeholder="Ask ArcaOS…" className="flex-1 bg-transparent outline-none text-white placeholder:text-slate-600 text-sm py-2" data-testid="ask-input" />
        <button onClick={() => submit()} disabled={busy || !q.trim()} className="bg-[#0055FF] text-white rounded-xl p-2.5 disabled:opacity-40 hover:-translate-y-0.5 transition-transform duration-300" data-testid="ask-send"><Send size={16} /></button>
      </div>
    </div>
  );
}
